import { action, computed, observable } from 'mobx';
import last from 'lodash/last';
import first from 'lodash/first';
import colors from '@honeybook/hbui/dist/styles/colors';
import {
    FlowStatus,
    IFlow,
    IFlowPublicConfiguration,
    SystemUserType,
    TFlowStatus
} from '~/types/model.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { _get } from '~/utils/lodash.utils';
import { IPublisher, IVariableResponse } from '~/types/server.types';
import type { CXChargeBlock } from '~/components/ClientExperience/OneFlow/models/cx-charge-block.model';
import type { CXServiceSelectionBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-selection-block.model';
import type { CXInvoiceBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-block.model';
import { BOOKABLE_BLOCK_TYPES } from '~/common/flows.constants';
import type { CXServiceSelectionItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-selection-item-block.model';
import type { CXInvoiceColumnHeaderBlock as CXInvoiceColumnHeaderBlockModel } from '~/components/ClientExperience/OneFlow/models/cx-invoice-column-header-block.model';
import type { CXInvoiceSummaryBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-summary-block.model';
import { BaseFlow } from '~/models/base-flow.model';
import type { CXPaymentBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-block.model';
import type { CXBaseBlock } from '~/components/ClientExperience/OneFlow/models/cx-base-block.model';
import type { CXScheduleContainerBlock } from '~/components/ClientExperience/OneFlow/models/cx-schedule-container-block.model';
import { TCXBlock } from '~/types/client-experience.types';
import type { CXLeadDetailsBlock } from '~/components/ClientExperience/OneFlow/models/cx-lead-details-block.model';
import type { CXPaymentScheduleBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-schedule-block.model';
import { FixedContactLinkedQuestionType } from '~/types/builder.types';
import { CXQuestionBlock } from '~/components/ClientExperience/OneFlow/models/cx-question-block.model';
import { DEMO_LEAD_FORM_ID } from '~/constants/lead-forms.consts';
import { TOneComposerSmartFields } from '~/common/SmartFields/smart-fields.types';

export class CXFlow extends BaseFlow<CXRootStore> {
    @observable revision_id: string;
    @observable smart_fields: TOneComposerSmartFields;
    @observable submitted_by_user: { first_name?: string; last_name?: string };
    @observable is_booked: boolean;
    @observable fullstory_force_record_cx: boolean;
    @observable flow_url: string;
    @observable view_only_flow_url: string;
    @observable publisher?: IPublisher;
    @observable public_configuration?: IFlowPublicConfiguration;

    rootStore: CXRootStore;

    constructor(flow: IFlow, rootStore: CXRootStore) {
        super(flow, rootStore);
        this.rootStore = rootStore;
        this.revision_id = flow.revision_id;
        this.submitted_by_user = flow.submitted_by_user;
        this.submitted_on = flow.submitted_on;
        this.is_booked = flow.is_booked;
        this.card_on_file_enabled = flow.card_on_file_enabled;
        this.fullstory_force_record_cx = flow.fullstory_force_record_cx;
        this.flow_url = flow.flow_url;
        this.view_only_flow_url = flow.view_only_flow_url;
        this.publisher = flow.publisher;
        this.public_configuration = flow.public_configuration;
    }

    @action
    setFlowStatus(newStatus: TFlowStatus) {
        this.status = newStatus;
    }

    @action
    updateVariables(variables: IVariableResponse[]) {
        this.activeRevision.updateVariables(variables);
    }

    @action
    setSubmittedOn(submittedOn: string) {
        this.submitted_on = submittedOn;
    }

    @action
    setSmartFields(smartFields: TOneComposerSmartFields) {
        this.smart_fields = smartFields;
    }

    @computed
    get leadDetailsBlock() {
        return this.blocks.find((block): block is CXLeadDetailsBlock =>
            block.isLeadDetailsBlock()
        );
    }

    @computed
    get hasPublicLeadDetailsBlock() {
        return !!this.leadDetailsBlock;
    }

    @computed
    get variables() {
        return this.activeRevision?.variables;
    }

    @computed
    get isBookable() {
        return this.blocks.some(block =>
            BOOKABLE_BLOCK_TYPES.includes(block.type)
        );
    }

    @computed
    get theme() {
        const { configuration } = this.rootStore.clientPortalConfigStore;
        const brandColor = _get(configuration, 'brand.brand_color');

        return {
            ...this._theme,
            styles: {
                ...this._theme.styles,
                accent_color:
                    this._theme.styles.accent_color ||
                    brandColor ||
                    colors.defaultBrandColor
            }
        };
    }

    @computed
    get isDemoLeadForm() {
        return this._id === DEMO_LEAD_FORM_ID;
    }

    @computed
    get company() {
        let currFlowCompany = this.rootStore.companyStore.byId(this.company_id);

        // get current company if store doesn't have the flow's company by id
        // in case we're in gallery preview
        if (!currFlowCompany) {
            currFlowCompany = this.rootStore.companyStore.byId(
                this.rootStore.sessionStore.sessionCompanyId
            );
        }
        return currFlowCompany;
    }

    @computed
    get isFlowCompleted() {
        return this.status === FlowStatus.Completed;
    }

    @computed
    get isViewOnlyShareable() {
        return !!this.view_only_flow_url && this.isInstance;
    }

    @computed
    get firstClient() {
        return first(this.flowRecipients);
    }

    @computed
    get invoiceContainerBlock(): CXInvoiceBlock {
        return this.blocks.find(
            blk => blk.type === 'invoice_container_block'
        ) as CXInvoiceBlock;
    }

    @computed
    get hasScheduleBlock() {
        return !!this.scheduleContainerBlock;
    }

    @computed
    get scheduleContainerBlock() {
        return this.blocks.find(
            b => b.type === 'schedule_container_block'
        ) as CXScheduleContainerBlock;
    }

    @computed
    get scheduleContainerBlockIndex() {
        return this.blocks.findIndex(
            b => b.type === 'schedule_container_block'
        );
    }

    @computed
    get uploadQuestionBlocks() {
        return this.blocks.filter(
            block =>
                block.isQuestionBlock &&
                (block as CXQuestionBlock).question.question_type ===
                    'upload_attachments'
        );
    }

    @computed
    get uploadQuestionBlocksVariablesIds() {
        return this.uploadQuestionBlocks.map(
            block => block.connected_variable_id
        );
    }

    @computed
    get hasInvoiceBlock() {
        return !!this.invoiceContainerBlock;
    }

    @computed
    get invoiceColumnHeaderBlock() {
        return this.blocks.find(
            blk => blk.type === 'invoice_column_header_block'
        ) as CXInvoiceColumnHeaderBlockModel;
    }

    @computed
    get invoiceSummaryBlock() {
        return this.blocks.find(
            blk => blk.type === 'invoice_summary_block'
        ) as CXInvoiceSummaryBlock;
    }

    @computed
    get sortedServiceSelectionBlocks() {
        return this.sortBlocksByPagesOrder(this.serviceSelectionBlocks);
    }

    @computed
    get serviceSelectionBlocks(): CXServiceSelectionBlock[] {
        return this.blocks.filter(
            blk => blk.type === 'service_selection_block'
        ) as CXServiceSelectionBlock[];
    }

    @computed
    get questionBlocks(): CXQuestionBlock[] {
        return this.blocks.filter(
            block => block.isQuestionBlock
        ) as CXQuestionBlock[];
    }

    @computed
    get clientSelectedServicesBlocks() {
        return this.serviceSelectionBlocks
            .filter(block => block.hasSelection)
            .flatMap(
                serviceSelectionBlock =>
                    serviceSelectionBlock.selectedServiceBlocks
            ) as CXServiceSelectionItemBlock[];
    }

    @computed
    get autoSelectedServicesBlocks() {
        return this.serviceSelectionBlocks
            .filter(block => !block.hasSelection && block.send_info_to_invoice)
            .flatMap(
                serviceSelectionBlock =>
                    serviceSelectionBlock.selectedServiceBlocks
            ) as CXServiceSelectionItemBlock[];
    }

    @computed
    get hasServiceSelectionBlocks() {
        return this.serviceSelectionBlocks.length > 0;
    }

    @computed
    get hasSessionSelectionBlock() {
        return !!this.sessionSelectionBlock;
    }

    @computed
    get hasChargeableServiceSelectionBlocks() {
        return this.serviceSelectionBlocks.some(
            serviceSelectionBlock => serviceSelectionBlock.isChargeable
        );
    }

    @computed
    get sessionSelectionBlock() {
        return this.serviceSelectionBlocks.find(
            block => block.isSessionSelection
        );
    }

    @computed
    get allSelectedServicesBlocks() {
        return this.serviceSelectionBlocks.reduce(
            (selectedBlocks, serviceSelectionBlock) => {
                return selectedBlocks.concat(
                    serviceSelectionBlock.selectedServiceBlocks
                );
            },
            []
        ) as CXServiceSelectionItemBlock[];
    }

    @computed
    get isAnyUserSelectionMade() {
        return this.serviceSelectionBlocks
            .filter(block => block.hasSelection)
            .some(
                serviceSelection =>
                    !!serviceSelection.selectedServiceBlocks.length
            );
    }

    @computed
    get paymentsBlocksCount() {
        return this.revision.paymentsBlocksCount;
    }

    @computed
    get hasPaidPayments() {
        return this.invoiceContainerBlock?.hasPaidPayments;
    }

    @computed
    get paidPayments() {
        return this.revision.paidPayments;
    }

    @computed
    get unpaidPayments() {
        return this.revision.unpaidPayments;
    }

    @computed
    get hasPaidPayment() {
        return this.revision.hasPaidPayments;
    }

    @computed
    get lastPageId() {
        return last(this.pages).page_id;
    }

    @computed
    get lastPaidPayment() {
        return this.revision.lastPaidPaymentBlock;
    }

    @computed
    get pendingPayments() {
        return this.revision.pendingPayments;
    }

    @computed
    get lastPendingPayment() {
        return this.revision.lastPendingPayment;
    }

    @computed
    get isZeroInvoice() {
        return this.upcomingPayment?.paymentAmount === 0;
    }

    @computed
    get overduePayments() {
        return this.revision.overduePayments;
    }

    @computed
    get isSomePaymentPaid() {
        return this.revision.isSomePaymentPaid;
    }

    @computed
    get isAllPaymentsNotRequestedMilestone() {
        return this.revision.isAllPaymentsNotRequestedMilestone;
    }

    @computed
    get upcomingPayment(): CXPaymentBlock {
        return this.revision.upcomingPayment;
    }

    get isClientMustPay() {
        return (
            !this.isSomePaymentPaid &&
            !this.isCanceledRecurringInvoice &&
            !this.isZeroInvoice &&
            !this.isAllPaymentsNotRequestedMilestone
        );
    }

    get isRecurringInvoice() {
        return this.invoiceContainerBlock?.isRecurringInvoice;
    }

    get isCanceledRecurringInvoice() {
        return (
            this.isRecurringInvoice &&
            !this.invoiceContainerBlock?.invoiceRecurringScheduleBlock?.isActive
        );
    }

    get pageWithInvoice() {
        return this.pages.find(p => !!p.invoiceContainerBlock);
    }

    @computed
    get paymentScheduleBlock() {
        return this.blocks.find(
            b => b.isPaymentScheduleBlock
        ) as CXPaymentScheduleBlock;
    }

    @computed
    get paymentsBlocks() {
        return this.revision.paymentsBlocks;
    }

    @computed
    get servicesBlock() {
        return this.blocks.find(b => b.isServicesBlock);
    }

    @computed
    get contractHtmlBlocks() {
        return this.blocks.filter(b => b.isContractHtmlBlock);
    }

    @computed
    get submitting_user() {
        if (this.submitted_by_user) {
            if (this.submitted_by_user.first_name) {
                return `${this.submitted_by_user.first_name} ${
                    this.submitted_by_user.last_name
                        ? this.submitted_by_user.last_name
                        : ''
                }`;
            }

            return null;
        }

        return null;
    }

    getMemberById(memberId) {
        return this.members.find(member => {
            return member._id === memberId;
        });
    }

    @computed
    get pendingApprovalBlocks() {
        return this.blocks.filter(
            block => (block as CXBaseBlock).isPendingApproval
        );
    }

    @computed
    get linkedPhoneQuestion() {
        return this.blocks.find(
            block =>
                block.isQuestionBlock &&
                (block as CXQuestionBlock).question.connected_property ===
                    FixedContactLinkedQuestionType.PhoneNumber
        );
    }

    get hasLinkedPhoneQuestion() {
        return !!this.linkedPhoneQuestion;
    }

    @computed
    get isFlowPendingApproval() {
        return this.pendingApprovalBlocks.length > 0;
    }

    sortBlocksByPagesOrder<T extends TCXBlock>(blocks: T[]) {
        return blocks.sort((a, b) => a.page.order - b.page.order);
    }

    @computed
    get sortedPendingApprovalBlocks() {
        return this.sortBlocksByPagesOrder(this.pendingApprovalBlocks);
    }

    @computed
    get isExpiredOrExpiredPreview() {
        const isSmafExpired = this.configuration?.auto_expiration?.expired;
        const { isExpiryPreview } =
            this.rootStore.flowViewUiStore.previewContext;
        return isSmafExpired || isExpiryPreview;
    }

    get expiresOn() {
        return this.configuration?.auto_expiration?.ends_on;
    }

    getUserTypeOnFlow(): SystemUserType {
        const {
            sessionStore: { authenticated, weakAuthenticated },
            userStore: { currUser }
        } = this.rootStore;

        if (authenticated) {
            return currUser.isPartOfCompany(this.company_id)
                ? 'vendor'
                : 'client';
        }

        if (weakAuthenticated) {
            return 'client';
        }

        return 'client';
    }

    @computed
    get chargeBlock(): CXChargeBlock {
        return this.blocks.find(
            blk => blk.type === 'charge_block'
        ) as CXChargeBlock;
    }

    get chargeBlockPageIndex(): number {
        let index = this.rootStore.flowViewUiStore.findPageIndex(
            this.chargeBlock?.page_id
        );
        return index;
    }

    @computed
    get isLastPayment(): boolean {
        return (
            this.revision.duePaymentsBlocksCount ===
            this.upcomingPayment?.paymentIndex
        );
    }

    @computed
    get isPublishedTemplate(): boolean {
        return this.public_configuration?.published_template;
    }

    @computed
    get areAllRequiredBlocksFilled(): boolean {
        const areAllRequiredQuestionsFilled = this.questionBlocks.every(
            questionBlock => !questionBlock.isPendingClientCompletion
        );
        const isSchedulerValid =
            !this.scheduleContainerBlock ||
            this.scheduleContainerBlock.isValidForSubmission;
        const areServiceSelectionsValid = this.serviceSelectionBlocks.every(
            serviceBlock => serviceBlock.isSelectionComplete
        );
        return (
            areAllRequiredQuestionsFilled &&
            isSchedulerValid &&
            areServiceSelectionsValid
        );
    }
}
